import { Badge, cn } from "@tcg/ui";
import {
  Banknote,
  CreditCard,
  Ellipsis,
  Loader2,
  NotebookPen,
  Percent,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useRegister } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import type { CartLine as Line } from "@/lib/types";

import { CartLine } from "./CartLine";
import { DiscountModal } from "./DiscountModal";
import { useCart } from "./hooks/useCart";
import { ItemBadges } from "./ItemBadges";
import { localized } from "./labels";
import { NoteModal } from "./NoteModal";
import { PriceOverrideModal } from "./PriceOverrideModal";
import { TotalsBar } from "./TotalsBar";

export type PayChoice = "cash" | "card" | "other";

/**
 * Right pane (design/05 §3.1): basket lines (+ "確認中…" rows of scans the server is still
 * checking), discounts, 小計 / 内消費税 / 合計 from the server, why it cannot be paid yet, and the
 * three big pay buttons (Stripe only behind its flag — R18).
 */
export function CartPane({ onPay }: { onPay: (choice: PayChoice) => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "ja";
  const cart = useCart();
  const register = useRegister();
  const [discountFor, setDiscountFor] = useState<Line | null | "cart">(null);
  const [priceFor, setPriceFor] = useState<Line | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const note = cart.cart?.note ?? null;
  const lines = cart.cart?.lines ?? [];
  const busy = cart.cart?.status === "paying";
  const empty = lines.length === 0 && cart.pending.length === 0;
  const stripe = register.data?.features.stripeTerminal === true;
  const cartDiscounts = (cart.cart?.discounts ?? []).filter((d) => d.scope === "cart");
  const blockers = cart.blockers.filter((b) => b !== "empty");

  return (
    <aside aria-labelledby="cart-title" className="flex min-h-0 flex-col border-l bg-background">
      <h2 className="flex items-center justify-between border-b px-3 py-2 text-base font-semibold">
        <span id="cart-title">{t("cart.title")}</span>
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          disabled={!cart.cart || busy}
          className="ml-auto flex min-h-pos-touch min-w-pos-touch items-center gap-2 rounded-md border px-3 text-base font-medium hover:bg-accent disabled:opacity-50"
        >
          <NotebookPen aria-hidden className="size-5" />
          {t("cart.note")}
        </button>
        {lines.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t("cart.clear_confirm"))) {
                void cart.clear();
              }
            }}
            className="flex min-h-pos-touch items-center gap-2 rounded-md px-3 text-base font-normal text-muted-foreground hover:bg-accent"
          >
            <Trash2 aria-hidden className="size-5" />
            {t("cart.clear")}
          </button>
        ) : null}
      </h2>
      {note ? (
        <p
          data-testid="cart-note"
          className="flex items-start gap-2 border-b bg-status-pending/10 px-3 py-2 text-sm"
        >
          <NotebookPen aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 break-words">{note}</span>
        </p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite">
        {empty ? (
          <p className="p-6 text-center text-muted-foreground">
            {cart.loading ? t("app.loading") : t("cart.empty")}
          </p>
        ) : null}
        <ul>
          {lines.map((line) => (
            <CartLine
              key={line.id}
              line={line}
              soldElsewhere={cart.soldElsewhere.has(line.id)}
              soldAt={cart.soldAt.get(line.id) ?? null}
              disabled={busy}
              onEditPrice={setPriceFor}
              onDiscount={setDiscountFor}
              onRemove={(l) => void cart.removeLine(l.id)}
              onQty={(l, qty) => void cart.setQty(l.id, qty)}
            />
          ))}
          {cart.pending.map((row) => (
            <li
              key={row.key}
              data-pending={row.status}
              className={cn(
                "flex items-start gap-2 border-b px-3 py-2",
                row.status === "failed" ? "bg-status-problem/10" : "bg-muted/60",
              )}
            >
              {row.status === "confirming" ? (
                <Loader2 aria-hidden className="mt-1 size-5 animate-spin" />
              ) : (
                <TriangleAlert aria-hidden className="mt-1 size-5 text-pos-problem" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {row.item ? localized(row.item.title, locale) : row.code}
                </p>
                {row.item ? (
                  <ItemBadges
                    productType={row.item.productType}
                    rawRank={row.item.rawRank}
                    consigned={row.item.isConsigned}
                    size="sm"
                  />
                ) : null}
                <p
                  role={row.status === "failed" ? "alert" : "status"}
                  className={cn(
                    "text-sm",
                    row.status === "failed"
                      ? "font-semibold text-pos-problem"
                      : "text-muted-foreground",
                  )}
                >
                  {row.status === "failed" ? row.error : t("sale.confirming")}
                </p>
              </div>
              {row.item ? (
                <MoneyText amount={row.item.price} className="text-muted-foreground" />
              ) : null}
            </li>
          ))}
        </ul>
        {cartDiscounts.map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between gap-2 border-b px-3 py-2 text-pos-problem"
          >
            <span className="flex items-center gap-1">
              <Percent aria-hidden className="size-4" />
              {t("cart.discount")} · {d.reason}
            </span>
            <span className="flex items-center gap-2">
              <MoneyText amount={d.amount} negative />
              <button
                type="button"
                aria-label={t("discount.remove")}
                onClick={() => void cart.removeDiscount(d.id)}
                className="flex size-10 items-center justify-center rounded-md border"
              >
                <X aria-hidden className="size-4" />
              </button>
            </span>
          </div>
        ))}
      </div>
      {lines.length > 0 ? (
        <div className="flex gap-2 border-t px-3 py-2">
          <button
            type="button"
            onClick={() => setDiscountFor("cart")}
            disabled={busy}
            className="flex min-h-12 items-center gap-2 rounded-md border px-4 text-base disabled:opacity-50"
          >
            <Percent aria-hidden className="size-4" />
            {t("cart.add_discount")}
          </button>
        </div>
      ) : null}
      <TotalsBar cart={cart.cart} />
      {blockers.length > 0 ? (
        <div
          role="alert"
          className="flex flex-col gap-1 border-t border-status-problem bg-status-problem/10 px-3 py-2 text-sm font-semibold text-pos-problem"
        >
          {blockers.map((b) => (
            <p key={b} className="flex items-center gap-1">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              {b === "sold_elsewhere"
                ? t("sale.sold_elsewhere_banner")
                : t(`blocker.${b}`, { defaultValue: t("blocker.precheck_failed") })}
            </p>
          ))}
        </div>
      ) : null}
      <div className={cn("grid gap-2 p-3", stripe ? "grid-cols-4" : "grid-cols-3")}>
        <PayButton
          onClick={() => onPay("cash")}
          disabled={!cart.canPay}
          label={t("cart.pay_cash")}
          icon={<Banknote aria-hidden />}
          primary
        />
        <PayButton
          onClick={() => onPay("card")}
          disabled={!cart.canPay}
          label={t("cart.pay_card")}
          icon={<CreditCard aria-hidden />}
        />
        {stripe ? (
          <PayButton
            onClick={() => undefined}
            disabled
            label={t("tender.stripe")}
            icon={<Badge variant="outline">{t("tender.stripe_coming")}</Badge>}
          />
        ) : null}
        <PayButton
          onClick={() => onPay("other")}
          disabled={!cart.canPay}
          label={t("cart.pay_other")}
          hint={t("cart.pay_other_hint")}
          icon={<Ellipsis aria-hidden />}
        />
      </div>
      <DiscountModal
        open={discountFor !== null}
        line={discountFor === "cart" ? null : discountFor}
        onClose={() => setDiscountFor(null)}
        onApply={cart.addDiscount}
      />
      <PriceOverrideModal
        line={priceFor}
        onClose={() => setPriceFor(null)}
        onApply={(line, price, reason) => cart.overridePrice(line.id, price, reason)}
        onReset={(line) => cart.resetPrice(line.id)}
      />
      {noteOpen ? (
        <NoteModal open current={note} onClose={() => setNoteOpen(false)} onSave={cart.setNote} />
      ) : null}
    </aside>
  );
}

function PayButton({
  label,
  hint,
  icon,
  onClick,
  disabled,
  primary,
}: {
  label: string;
  hint?: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex min-h-20 flex-col items-center justify-center gap-1 rounded-lg border text-lg font-semibold [&_svg]:size-6 disabled:cursor-not-allowed disabled:opacity-40",
        primary ? "border-primary bg-primary text-primary-foreground" : "bg-background",
      )}
    >
      {icon}
      <span>{label}</span>
      {hint ? <span className="text-xs font-normal">{hint}</span> : null}
    </button>
  );
}
